/**
 * talentPool.api.ts — client for the standalone Talent Pool Search (local crypto-lever pool).
 *
 * Hits apps/server at /api/v2/talent-pool/* (gated by the `crypto-local-search` org preset). apps/server
 * owns NL→DSL (/plan) and proxies execution (/count, /search, /resolve, /enums, /examples, /candidates)
 * to the EC2 crypto-lever backend. The QueryNode/Filter/Group types below are the UI↔server contract —
 * ported from crypto-lever-search/preprocess/src/dslCompiler.ts; keep them in sync.
 */

import { api } from '@/lib/http';

// ── crypto-lever query AST (the editable tree) ──
export type Combinator = 'and' | 'or';
export type WhenScope = 'current' | 'past' | 'any';
export type MatchType = 'any' | 'all';

export interface Filter {
    field: string;
    values?: string[];
    similarity?: number; // 0..1 (value fields only)
    matchType?: MatchType;
    negate?: boolean;
    gte?: number; // ranges
    lte?: number;
    exactLevels?: boolean; // seniority/study_level: exact picks vs "this level and up"
    when?: WhenScope; // temporal scope for experience-based fields
    proficiency?: string; // language: minimum proficiency floor
}

export interface Group {
    type: 'group';
    combinator: Combinator;
    negate?: boolean;
    scope?: 'experience' | 'education' | null;
    children: QueryNode[];
}

export type QueryNode = Group | Filter;

export function isGroup(node: QueryNode): node is Group {
    return (node as Group).type === 'group';
}

export const emptyRoot = (): Group => ({ type: 'group', combinator: 'and', children: [] });

// ── result shapes (crypto-lever _source; see candidates_v2 mapping) ──
export type ExperienceEntry = {
    position_title?: string;
    company_name?: string;
    company_domain?: string;
    company_linkedin?: string;
    company_industry?: string | null;
    title_seniority?: string | null;
    is_manager?: boolean | number | null;
    date_from?: string;
    date_to?: string;
    date_from_year?: number | null;
    date_to_year?: number | null;
    active_experience?: number;
    location?: string;
    description?: string;
};

export type EducationEntry = {
    institution_name?: string;
    degree?: string;
    field_of_study?: string;
    date_from_year?: number | null;
    date_to_year?: number;
};

export type SkillItem = { skill: string; years?: number; is_primary?: boolean };
export type LanguageItem = { language: string; proficiency?: string; proficiency_rank?: number };
export type CertItem = { title: string; issuer?: string };

/** Per-candidate, per-criterion judgement (3-state, aligned with criteria[] order). */
export type CriteriaResult = {
    verdict: 'yes' | 'maybe' | 'no'; // met / partial / not-met
    explanation?: string;
};

export type CandidateResult = {
    id: string;
    full_name?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    headline?: string | null;
    summary?: string | null;
    active_experience_title?: string | null;
    active_title_seniority?: string | null;
    active_is_manager?: boolean | null;
    active_department?: string | null;
    num_jobs?: number | null;
    avg_tenure_months?: number | null;
    current_role_months?: number | null;
    job_hopper?: boolean | null;
    location_full?: string | null;
    location_city?: string | null;
    location_city_canonical?: string | null;
    location_country?: string | null;
    location_country_iso2?: string | null;
    total_experience_duration_months?: number | null;
    primary_industry?: string | null;
    industries?: string[];
    company_sizes?: string[];
    skills_canonical?: string[];
    inferred_skills?: string[];
    skills?: SkillItem[];
    languages?: LanguageItem[];
    certifications?: CertItem[];
    experience?: ExperienceEntry[];
    education?: EducationEntry[];
    emails?: string[]; // PII — display only
    phone?: string | null; // PII — display only
    open_to_work?: boolean | null;
    linkedin_url?: string | null;
    github_url?: string | null;
    portfolio_url?: string | null;
    twitter_url?: string | null;
    crunchbase_url?: string | null;
    observed_at?: string | null;
    _evidence?: string[];
    _timezone?: string | null;
    _score?: number | null;
    // present when score:true (criteria scored by apps/server via Gemini)
    criteria_met?: number;
    criteria_total?: number;
    criteria_verdicts?: boolean[]; // legacy binary form (kept for back-compat)
    criteria_results?: CriteriaResult[]; // 3-state verdict + per-criterion explanation
    match_rate?: number | null; // 0–100 weighted score across criteria (order = importance)
    criteria_summary?: string | null; // 1–2 sentence read of fit
};

export type PlanResponse = { tree: Group; criteria: string[]; model?: string; cached?: boolean; planMs?: number };
export type CountResponse = { total: number; ms?: number };
export type SearchResponse = {
    total: number;
    results: CandidateResult[];
    relaxation?: string | null;
    criteria?: string[];
    criteria_labels?: string[];
    scored?: boolean;
    ms?: number;
    scoreMs?: number;
    // async scoring: results come back unscored + a scoreJobId to poll (see getScoreStatus)
    scoring?: boolean;
    scoreJobId?: string;
    scoreTotal?: number;
    scoreError?: string;
};

// one candidate's soft-criteria score, streamed back progressively by the score workers
export type ScoredRow = {
    cursor: string; // result _id — the poll cursor (pass the max as `after`)
    candidateId: string;
    criteria_results: CriteriaResult[];
    criteria_verdicts: boolean[];
    criteria_met: number;
    criteria_total: number;
    match_rate: number | null;
    criteria_summary: string | null;
};
export type ScoreProgress = {
    status: 'scoring' | 'done' | 'error';
    total: number;
    scored: number;
    incomplete?: boolean; // scoring finished but some candidates never got scored (a lost chunk)
    criteria?: string[];
    criteria_labels?: string[];
    results: ScoredRow[]; // only rows scored SINCE the `after` cursor
};
export type EntitySuggestion = { canonical?: string; key?: string; confidence?: number; via?: string; label?: string };
export type EnumsResponse = {
    title_seniority?: string[];
    departments?: string[];
    study_levels?: string[];
    company_sizes?: string[];
    industries?: string[];
};

const BASE = '/v2/talent-pool';

export async function planQuery(query: string, current?: { tree: QueryNode; criteria: string[] }): Promise<PlanResponse> {
    const { data } = await api.post<PlanResponse>(`${BASE}/plan`, { query, current }, { timeout: 130_000 });
    return data;
}

export async function countTree(tree: QueryNode): Promise<CountResponse> {
    const { data } = await api.post<CountResponse>(`${BASE}/count`, { tree });
    return data;
}

export async function searchTree(opts: {
    tree: QueryNode;
    page?: number;
    query?: string;
    criteria?: string[];
    score?: boolean;
}): Promise<SearchResponse> {
    const { data } = await api.post<SearchResponse>(`${BASE}/search`, {
        tree: opts.tree,
        page: opts.page ?? 1,
        query: opts.query,
        criteria: opts.criteria,
        score: opts.score ?? false,
    });
    return data;
}

/** Poll progressive soft-criteria scores for an async search run. `after` = the max `cursor` seen so far. */
export async function getScoreStatus(jobId: string, after?: string): Promise<ScoreProgress> {
    const { data } = await api.get<ScoreProgress>(`${BASE}/scores/${encodeURIComponent(jobId)}`, { params: after ? { after } : undefined });
    return data;
}

export async function fieldExamples(field: string, values: string[], similarity: number): Promise<string[]> {
    const { data } = await api.post<{ examples?: string[] }>(`${BASE}/examples`, { field, values, similarity });
    return data.examples ?? [];
}

export async function resolveEntity(field: string, q: string, n = 8): Promise<EntitySuggestion[]> {
    const { data } = await api.get<{ suggestions?: EntitySuggestion[] }>(`${BASE}/resolve`, {
        params: { field, q, n },
    });
    return data.suggestions ?? [];
}

export async function getEnums(): Promise<EnumsResponse> {
    const { data } = await api.get<EnumsResponse>(`${BASE}/enums`);
    return data;
}

export async function getCandidate(id: string): Promise<CandidateResult | null> {
    const { data } = await api.get<{ id: string; enriched: CandidateResult | null }>(
        `${BASE}/candidates/${encodeURIComponent(id)}`,
    );
    return data.enriched ? { ...data.enriched, id: data.id } : null;
}

// ── recruiting history (live Lever ATS Mongo; the "application data" the crypto-lever drawer shows) ──
export type HistoryQA = { q?: string | null; a?: string | null };
export type HistoryNote = { text?: string | null; author?: string | null; at?: string | null; stage?: string | null; score?: number | null; secret?: boolean };
export type HistoryInterview = { subject?: string | null; stage?: string | null; date?: string | null; interviewers?: string[] };
export type HistoryFeedback = { author?: string | null; score?: number | null; completed?: boolean; fields?: { q?: string | null; value?: string | null }[] };
export type HistoryStage = { stage?: string | null; at?: string | null; by?: string | null };
export type ApplicationEntry = {
    opportunityId: string;
    appliedAt?: string | null;
    source?: string | null;
    tags?: string[];
    archived?: boolean;
    owner?: string | null;
    currentStage?: string | null;
    stageHistory?: HistoryStage[];
    job?: { title?: string | null; department?: string | null; team?: string | null; location?: string | null; workplaceType?: string | null } | null;
    application?: { questions?: HistoryQA[]; comments?: string | null; company?: string | null } | null;
    notes?: HistoryNote[];
    interviews?: HistoryInterview[];
    feedback?: HistoryFeedback[];
    interviewScore?: number | null;
    offer?: { status?: string | null; sentAt?: string | null; fields?: { label?: string | null; value?: string | null }[] } | null;
    resume?: { ext?: string | null } | null;
};
export type CandidateHistory = {
    applications: ApplicationEntry[];
    summary: { applications: number; interviews: number; offers: number; notes: number; firstSeen?: string | null };
    unavailable?: boolean;
};

export async function getCandidateHistory(id: string): Promise<CandidateHistory> {
    const { data } = await api.get<CandidateHistory>(`${BASE}/candidates/${encodeURIComponent(id)}/history`);
    return data;
}

export type ResumeLink = { url: string; ext?: string };

/** Latest résumé for a contact → short-lived presigned inline URL. */
export async function getResumeUrl(contactId: string): Promise<ResumeLink> {
    const { data } = await api.get<ResumeLink>(`${BASE}/candidates/${encodeURIComponent(contactId)}/resume`);
    return data;
}

/** A specific application's résumé → short-lived presigned inline URL. */
export async function getOppResumeUrl(oppId: string): Promise<ResumeLink> {
    const { data } = await api.get<ResumeLink>(`${BASE}/resume/opp/${encodeURIComponent(oppId)}`);
    return data;
}

/**
 * Latest résumé bytes, streamed same-origin through apps/server (the S3 bucket
 * has no CORS rule for our origin, so the browser can't fetch the presigned URL
 * directly). Used to render .docx client-side.
 */
export async function getResumeBytes(contactId: string): Promise<ArrayBuffer> {
    const { data } = await api.get<ArrayBuffer>(`${BASE}/candidates/${encodeURIComponent(contactId)}/resume/raw`, {
        responseType: 'arraybuffer',
    });
    return data;
}

/** A specific application's résumé bytes, streamed same-origin (see getResumeBytes). */
export async function getOppResumeBytes(oppId: string): Promise<ArrayBuffer> {
    const { data } = await api.get<ArrayBuffer>(`${BASE}/resume/opp/${encodeURIComponent(oppId)}/raw`, {
        responseType: 'arraybuffer',
    });
    return data;
}

// ── saved searches (per-org, per-user; persisted in apps/server core Mongo) ──
export type SavedSearch = {
    _id: string;
    name: string;
    nlQuery: string;
    tree: Group;
    criteria: string[];
    lastCount?: number | null;
    lastRunAt?: string | null;
    createdAt?: string;
    updatedAt?: string;
};

export type SavedSearchInput = {
    name: string;
    nlQuery?: string;
    tree: Group;
    criteria?: string[];
    lastCount?: number | null;
};

export async function listSavedSearches(): Promise<SavedSearch[]> {
    const { data } = await api.get<{ savedSearches: SavedSearch[] }>(`${BASE}/saved-searches`);
    return data.savedSearches ?? [];
}

export async function createSavedSearch(input: SavedSearchInput): Promise<SavedSearch> {
    const { data } = await api.post<{ savedSearch: SavedSearch }>(`${BASE}/saved-searches`, input);
    return data.savedSearch;
}

export async function getSavedSearch(id: string): Promise<SavedSearch | null> {
    const { data } = await api.get<{ savedSearch: SavedSearch | null }>(`${BASE}/saved-searches/${encodeURIComponent(id)}`);
    return data.savedSearch ?? null;
}

export async function updateSavedSearch(id: string, patch: Partial<SavedSearchInput>): Promise<SavedSearch> {
    const { data } = await api.patch<{ savedSearch: SavedSearch }>(`${BASE}/saved-searches/${encodeURIComponent(id)}`, patch);
    return data.savedSearch;
}

export async function deleteSavedSearch(id: string): Promise<void> {
    await api.delete(`${BASE}/saved-searches/${encodeURIComponent(id)}`);
}
